import { SmallButton } from "@rpforms/shared/build/components/layout/Button";
import { createOfferFromForms, IFormPayload } from "@rpforms/shared/build/utilities";
import {
    generateDoorReport,
    IDoorReport,
} from "@rpforms/shared/build/utilities/doorReportGenerator";
import React, { useEffect, useMemo, useRef } from "react";
import ReactDOMServer from "react-dom/server";
import { Modal, Spinner } from "react-bootstrap";
import styled from "styled-components";

const ReportContainer = styled.div.attrs({ className: "report-container" })`
    padding: 12px 6px 0 6px;

    @media print {
        padding: 0;
    }
`;

const ReportHeader = styled.div.attrs({ className: "report-header" })`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    margin-bottom: 12px;
`;

const ReportTitle = styled.div.attrs({ className: "report-title" })`
    font-size: 18px;
    font-weight: bold;
`;

const ReportMeta = styled.div.attrs({ className: "report-meta" })`
    color: #666;
    font-size: 13px;
`;

const GroupCard = styled.div.attrs({ className: "group-card" })`
    border: 1px solid #e6e6e6;
    border-radius: 6px;
    margin-bottom: 12px;
    overflow: hidden;
`;

const GroupHeader = styled.div.attrs({ className: "group-header" })`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: #f7f7f7;
    font-weight: bold;
`;

const GroupBody = styled.div.attrs({ className: "group-body" })`
    padding: 8px 12px;
`;

const ItemRow = styled.div.attrs({ className: "item-row" })`
    padding: 6px 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
        border-bottom: none;
    }
`;

const ItemTitle = styled.div.attrs({ className: "item-title" })`
    font-weight: 600;
`;

const ItemMeta = styled.div.attrs({ className: "item-meta" })`
    color: #666;
    font-size: 13px;
    margin-top: 2px;
`;

const EmptyState = styled.div`
    padding: 24px 12px;
    color: #666;
    text-align: center;
`;

interface IDoorReportModalProps {
    formEntry: any;
    show?: boolean;
    onHide: () => void;
    loading?: boolean;
    autoPrint?: boolean;
}

const stripHtml = (html: string): string => {
    if (!html || typeof html !== "string") {
        return "";
    }
    return html.replace(/<[^>]*>/g, "").trim();
};

const decodeHtmlEntities = (text: string): string => {
    if (!text || typeof text !== "string") {
        return text;
    }
    const entities: { [key: string]: string } = {
        "&uuml;": "ü",
        "&Uuml;": "Ü",
        "&auml;": "ä",
        "&Auml;": "Ä",
        "&ouml;": "ö",
        "&Ouml;": "Ö",
        "&szlig;": "ß",
        "&nbsp;": " ",
        "&amp;": "&",
        "&lt;": "<",
        "&gt;": ">",
        "&quot;": '"',
        "&#39;": "'",
    };
    let decoded = text;
    for (const [entity, char] of Object.entries(entities)) {
        decoded = decoded.replace(new RegExp(entity, "g"), char);
    }
    decoded = decoded.replace(/&#(\d+);/g, (_, code) => String.fromCharCode(parseInt(code, 10)));
    decoded = decoded.replace(/&#x([0-9a-fA-F]+);/g, (_, code) =>
        String.fromCharCode(parseInt(code, 16))
    );
    return decoded;
};

const cleanText = (text: string): string => {
    if (!text) {
        return "";
    }
    return decodeHtmlEntities(stripHtml(text));
};

const renderItem = (item) => {
    const title = cleanText(item.title);
    const description = cleanText(item.description);
    const hasDescription = description && description.trim();
    return (
        <ItemRow key={`${item.nr}-${title}-${item.qty}`}>
            <ItemTitle>
                {item.nr}. {title}
            </ItemTitle>
            <ItemMeta>
                Menge: {item.qty} {item.unit || ""}
            </ItemMeta>
            {hasDescription && <ItemMeta>{description}</ItemMeta>}
        </ItemRow>
    );
};

const renderReport = (report: IDoorReport | null, hasData: boolean) => {
    if (!hasData) {
        return <EmptyState>Keine Tür-Report Daten vorhanden.</EmptyState>;
    }
    if (!report) {
        return <EmptyState>Keine Positionen für den Tür-Report vorhanden.</EmptyState>;
    }

    return (
        <>
            {report.doors.map((door) => (
                <GroupCard key={door.doorNumber}>
                    <GroupHeader>
                        <span>{door.doorLabel}</span>
                        <span>{door.items.length} Positionen</span>
                    </GroupHeader>
                    <GroupBody>{door.items.map(renderItem)}</GroupBody>
                </GroupCard>
            ))}
            {report.unassignedItems.length > 0 && (
                <GroupCard>
                    <GroupHeader>
                        <span>Ohne Zuordnung</span>
                        <span>{report.unassignedItems.length} Positionen</span>
                    </GroupHeader>
                    <GroupBody>{report.unassignedItems.map(renderItem)}</GroupBody>
                </GroupCard>
            )}
        </>
    );
};

const DoorReportPrintView = ({
    report,
    hasData,
    reportTitle,
    reportMeta,
}: {
    report: IDoorReport | null;
    hasData: boolean;
    reportTitle: string;
    reportMeta: string;
}) => {
    return (
        <div className="door-report-print-view">
            <ReportContainer>
                <ReportHeader>
                    <ReportTitle>{reportTitle}</ReportTitle>
                    <ReportMeta>
                        {reportMeta}
                        {report && ` · ${report.totalItems} Positionen`}
                        {report &&
                            report.unassignedItems.length > 0 &&
                            ` · ${report.unassignedItems.length} ohne Zuordnung`}
                    </ReportMeta>
                </ReportHeader>
                {renderReport(report, hasData)}
            </ReportContainer>
        </div>
    );
};

export const DoorReportModal: React.FC<IDoorReportModalProps> = ({
    formEntry,
    show = false,
    onHide,
    loading = false,
    autoPrint = false,
}) => {
    const printedRef = useRef(false);
    const normalizedEntry = useMemo(() => {
        if (!formEntry) {
            return null;
        }
        if (typeof formEntry.form_data === "string") {
            try {
                return { ...formEntry, form_data: JSON.parse(formEntry.form_data) };
            } catch (e) {
                return { ...formEntry, form_data: null };
            }
        }
        return formEntry;
    }, [formEntry]);

    const offerData = useMemo(() => {
        if (!normalizedEntry) {
            return null;
        }
        let schemaData;
        try {
            const rawSchema =
                normalizedEntry?.latest_revision?.form_data ||
                normalizedEntry?.form_revision?.form_data;
            schemaData = typeof rawSchema === "string" ? JSON.parse(rawSchema) : rawSchema;
        } catch (e) {
            return null;
        }

        let entryValues: any = normalizedEntry?.form_data || {};
        if (typeof entryValues === "string") {
            try {
                entryValues = JSON.parse(entryValues);
            } catch (e) {
                entryValues = {};
            }
        }

        const applyEntryValues = (fields = []) => {
            return fields.map((field) => {
                const nextField = { ...field };
                if (
                    entryValues &&
                    nextField?.hashCode &&
                    Object.prototype.hasOwnProperty.call(entryValues, nextField.hashCode)
                ) {
                    nextField.value = entryValues[nextField.hashCode];
                }
                if (nextField?.type === "LeistungsV") {
                    const notesKey = `${nextField.hashCode}__notes`;
                    if (entryValues && entryValues[notesKey]) {
                        nextField.notes = entryValues[notesKey];
                    }
                }
                if (nextField?.type === "TableField" && nextField.subfields) {
                    nextField.subfields = nextField.subfields.map((subfield) => {
                        const nextSubfield = { ...subfield };
                        if (nextSubfield?.type === "InventoryItemSelectTableRow") {
                            const notesKey = `${nextSubfield.hashCode}__notes`;
                            if (entryValues && entryValues[notesKey]) {
                                nextSubfield.notes = entryValues[notesKey];
                            }
                        }
                        return nextSubfield;
                    });
                }
                if (nextField.subfields && nextField.subfields.length > 0) {
                    nextField.subfields = applyEntryValues(nextField.subfields);
                }
                return nextField;
            });
        };

        const formPayload: IFormPayload = {
            fields: applyEntryValues(schemaData?.fields || []),
            form: {
                name: normalizedEntry?.form?.name || normalizedEntry?.form_name || "",
                endpoint: "",
            },
        };

        const invoicePositionsListField = formPayload.fields.find(
            (field) => field.type === "InvoicePositionsListField" && field.value
        );
        if (invoicePositionsListField) {
            let invoicePositions = [];
            try {
                if (typeof invoicePositionsListField.value === "string") {
                    const parsed = JSON.parse(invoicePositionsListField.value);
                    invoicePositions = parsed.invoicePositions || parsed;
                } else if (invoicePositionsListField.value?.invoicePositions) {
                    invoicePositions = invoicePositionsListField.value.invoicePositions;
                }
            } catch (e) {
                // ignore parse errors
            }
            if (invoicePositions && invoicePositions.length > 0) {
                return {
                    invoicePositions,
                    discount: invoicePositionsListField.value?.discount,
                    vat: invoicePositionsListField.value?.vat,
                    shippingInCents: invoicePositionsListField.value?.shippingInCents,
                    notice: invoicePositionsListField.value?.notice,
                    notice2: invoicePositionsListField.value?.notice2,
                };
            }
        }

        try {
            const result = createOfferFromForms([formPayload], {
                defaultUnit: "repair",
                defaultPrice: 0,
                includeHintsInDescription: true,
            });
            return result.offerData;
        } catch (e) {
            return null;
        }
    }, [normalizedEntry]);

    const hasData = !!offerData?.invoicePositions?.length;

    const report = useMemo(() => {
        if (!hasData || !normalizedEntry) {
            return null;
        }
        const syntheticEntry = {
            ...normalizedEntry,
            form_data: {
                door_report_invoice: offerData,
            },
        };
        return generateDoorReport(syntheticEntry);
    }, [normalizedEntry, offerData, hasData]);

    useEffect(() => {
        if (!show) {
            document.body.classList.remove("print-door-report");
        }
    }, [show]);

    const reportTitle = cleanText(
        report?.entityName || normalizedEntry?.entity?.name || "Tür-Report"
    );
    const reportMeta = report
        ? cleanText(`${report.formEntryName || ""}`).trim()
        : cleanText(normalizedEntry?.name || "");

    useEffect(() => {
        printedRef.current = false;
    }, [formEntry?.id]);

    const printReportViaIframe = () => {
        const html = ReactDOMServer.renderToString(
            <DoorReportPrintView
                report={report}
                hasData={hasData}
                reportTitle={reportTitle}
                reportMeta={reportMeta}
            />
        );

        const iframe = document.createElement("iframe");
        iframe.setAttribute("aria-hidden", "true");
        iframe.style.position = "fixed";
        iframe.style.right = "0";
        iframe.style.bottom = "0";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.style.border = "0";
        document.body.appendChild(iframe);

        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc) {
            document.body.removeChild(iframe);
            onHide();
            return;
        }

        doc.open();
        doc.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Tür-Report</title>
    <style>
      body { margin: 0; font-family: Arial, sans-serif; color: #111; }
      .door-report-print-view { padding: 20px; }
      .report-container { padding: 12px 6px 0 6px; }
      h1, h2 { margin: 0; }
      .item-meta { color: #666; font-size: 13px; }
      .group-card { border: 1px solid #e6e6e6; border-radius: 6px; margin-bottom: 12px; overflow: hidden; }
      .group-header { display: flex; justify-content: space-between; align-items: center; padding: 8px 12px; background: #f7f7f7; font-weight: bold; }
      .group-body { padding: 8px 12px; }
      .item-row { padding: 6px 0; border-bottom: 1px solid #f0f0f0; }
      .item-row:last-child { border-bottom: none; }
      .item-title { font-weight: 600; }
      .report-header { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; margin-bottom: 12px; }
      .report-title { font-size: 18px; font-weight: bold; }
      .report-meta { color: #666; font-size: 13px; }
    </style>
  </head>
  <body>${html}</body>
</html>`);
        doc.close();

        const iframeWindow = iframe.contentWindow;
        const cleanup = () => {
            if (iframe.parentNode) {
                iframe.parentNode.removeChild(iframe);
            }
            onHide();
        };

        const parentFocusCleanup = () => {
            window.removeEventListener("focus", parentFocusCleanup);
            cleanup();
        };

        window.addEventListener("focus", parentFocusCleanup, { once: true });

        if (iframeWindow) {
            iframeWindow.onafterprint = cleanup;
            iframeWindow.focus();
            iframeWindow.print();
        } else {
            cleanup();
        }
    };

    useEffect(() => {
        if (!autoPrint || !show || !report || loading || printedRef.current) {
            return;
        }

        printReportViaIframe();
        printedRef.current = true;
    }, [autoPrint, show, report, loading, hasData, reportTitle, reportMeta, onHide]);

    const handlePrint = () => {
        document.body.classList.add("print-door-report");
        document.documentElement.classList.add("print-door-report");
        const cleanup = () => {
            document.body.classList.remove("print-door-report");
            document.documentElement.classList.remove("print-door-report");
            onHide();
        };
        window.addEventListener("afterprint", cleanup, { once: true });
        window.print();
    };

    if (autoPrint) {
        return null;
    }

    return (
        <Modal show={show} onHide={onHide} size="lg" className="door-report-print">
            <Modal.Header closeButton className="hide-print">
                <Modal.Title>Tür-Report</Modal.Title>
            </Modal.Header>
            <Modal.Body className="print-area">
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" role="status">
                            <span className="sr-only">Laden...</span>
                        </Spinner>
                        <div className="mt-2">Tür-Report wird geladen...</div>
                    </div>
                ) : (
                    <ReportContainer>
                        <ReportHeader>
                            <ReportTitle>{reportTitle}</ReportTitle>
                            <ReportMeta>
                                {reportMeta}
                                {report && ` · ${report.totalItems} Positionen`}
                                {report &&
                                    report.unassignedItems.length > 0 &&
                                    ` · ${report.unassignedItems.length} ohne Zuordnung`}
                            </ReportMeta>
                        </ReportHeader>
                        {renderReport(report, hasData)}
                    </ReportContainer>
                )}
            </Modal.Body>
            <Modal.Footer className="hide-print">
                <SmallButton onClick={handlePrint}>Drucken</SmallButton>
                <SmallButton onClick={onHide}>Schließen</SmallButton>
            </Modal.Footer>
        </Modal>
    );
};
